// API configuration for Gaia frontend

// Determine if we're running in production (accessed via domain)
const isProduction = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';

// Capacitor native apps load from http://localhost or capacitor://localhost
// (no port) — they need an absolute backend URL since there's no web server
// to proxy through. We detect this by checking if we're on localhost without
// a port, since Vite dev always runs on a specific port (3000, 5173, etc.).
const isCapacitorNative = typeof window !== 'undefined' && (
  window.Capacitor?.isNativePlatform?.() === true ||
  (window.location.hostname === 'localhost' && !window.location.port)
);

// Use relative paths - Vite proxy handles routing to backend in development
const getBackendUrl = () => {
  if (import.meta.env.VITE_API_BASE_URL !== undefined) {
    return import.meta.env.VITE_API_BASE_URL;
  }
  // Native app needs an absolute URL to reach the backend
  if (isCapacitorNative) {
    return import.meta.env.VITE_NATIVE_BACKEND_URL || 'http://localhost:8090';
  }
  // Empty string = use relative URLs, which Vite proxies to backend
  return '';
};

const normalizeWsBase = (rawValue) => {
  if (!rawValue) {
    return '';
  }

  let value = String(rawValue).trim();
  if (!value) {
    return '';
  }

  if (value.startsWith('//')) {
    value = `${window.location.protocol}${value}`;
  }

  try {
    const url = new URL(value, window.location.origin);
    if (url.protocol === 'http:') {
      url.protocol = 'ws:';
    } else if (url.protocol === 'https:') {
      url.protocol = 'wss:';
    }
    return url.toString().replace(/\/$/, '');
  } catch {
    if (value.startsWith('http://')) {
      return `ws://${value.slice('http://'.length).replace(/\/$/, '')}`;
    }
    if (value.startsWith('https://')) {
      return `wss://${value.slice('https://'.length).replace(/\/$/, '')}`;
    }
    return value.replace(/\/$/, '');
  }
};

const getWsUrl = () => {
  const explicitWs = import.meta.env.VITE_WS_BASE_URL;
  if (explicitWs) {
    return normalizeWsBase(explicitWs);
  }
  const apiBase = import.meta.env.VITE_API_BASE_URL;
  if (apiBase) {
    return normalizeWsBase(apiBase);
  }
  // Native app needs absolute WebSocket URL (same backend as HTTP)
  if (isCapacitorNative) {
    const nativeBackend = import.meta.env.VITE_NATIVE_BACKEND_URL || 'http://localhost:8090';
    return normalizeWsBase(nativeBackend);
  }
  // Use current origin - Vite proxy handles routing to backend in dev
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}`;
};

// STT service - always use /stt prefix (dev proxies to 8001, prod uses cloudflared)
const getSttUrl = () => {
  if (import.meta.env.VITE_STT_BASE_URL) {
    return import.meta.env.VITE_STT_BASE_URL;
  }
  // Always use /stt prefix - backend or cloudflared handles routing
  // Note: The /stt prefix is required for all endpoints
  if (isProduction) return '';
  // Native WebView has no Vite proxy - needs an absolute STT URL, mirroring
  // getBackendUrl's native handling.
  if (isCapacitorNative) {
    return import.meta.env.VITE_NATIVE_STT_BASE_URL || 'http://localhost:8001/stt';
  }
  // Dev: same-origin /stt rides the Vite proxy to THIS worktree's STT
  // container (hardcoding localhost:8001 only reached the slot-0 stack).
  return typeof window !== 'undefined'
    ? `${window.location.origin}/stt`
    : 'http://localhost:8001/stt';
};

export const API_CONFIG = {
  // Base URLs
  BACKEND_URL: getBackendUrl(),
  WS_BASE_URL: getWsUrl(),
  STT_BASE_URL: getSttUrl(),
  
  // API endpoints
  get HEALTH_ENDPOINT() {
    return `${this.BACKEND_URL}/api/health`;
  },
  
  get CHAT_ENDPOINT() {
    return `${this.BACKEND_URL}/api/chat`;
  },
  
  get TEST_ENDPOINT() {
    return `${this.BACKEND_URL}/api/test`;
  },
  
  get TTS_ENDPOINT() {
    return `${this.BACKEND_URL}/api/tts`;
  },
  
  get CAMPAIGNS_ENDPOINT() {
    return `${this.BACKEND_URL}/api/campaigns`;
  },
  
  get IMAGES_ENDPOINT() {
    return `${this.BACKEND_URL}/api/images`;
  },
  
  // WebSocket endpoints
  get WS_CHAT_STREAM() {
    return `${this.WS_BASE_URL}/api/chat/stream`;
  },
  
  get VOICE_ACTIVITY_ENDPOINT() {
    // Use STT_BASE_URL when set (handles Firebase Hosting where no nginx proxy exists)
    if (this.STT_BASE_URL) {
      return `${this.STT_BASE_URL}/voice-activity`;
    }
    return isProduction ? '/stt/voice-activity' : 'http://localhost:8001/stt/voice-activity';
  },
  
  // Configuration options
  REQUEST_TIMEOUT: 30000, // 30 seconds
  WS_RECONNECT_DELAY: 5000, // 5 seconds
  MAX_RETRIES: 3,
  
  // Feature flags
  ENABLE_AUDIO: import.meta.env.VITE_ENABLE_AUDIO !== 'false',
  USE_OPENAPI: true,  // Always use OpenAPI now that protobuf is removed
  // Auto-submit player turns: when enabled, player submissions go directly to backend
  // instead of requiring DM approval. Useful for solo playtesting from player view.
  AUTO_SUBMIT_PLAYER_TURNS: import.meta.env.VITE_AUTO_SUBMIT_PLAYER_TURNS === 'true',

  // Submit ack: when enabled, submit_turn uses emitWithAck for server confirmation
  ENABLE_SUBMIT_ACK: import.meta.env.VITE_ENABLE_SUBMIT_ACK !== 'false',

  // V3 dialog action: when enabled, the player UI surfaces "Talk to <NPC>"
  // affordances that submit a turn with route="dialog" → backend dispatches
  // to dialog_turn_runner which streams a Doppelganger reply and skips the
  // heavy analyze/scene-flow/persist stages. Dark-launched off by default.
  ENABLE_DIALOG_ACTION: import.meta.env.VITE_ENABLE_DIALOG_ACTION === 'true',

  // Cinematic 3D scene diorama (BOU-1907): when enabled, the desktop PlayerViewV2
  // renders the current scene as a depth-displaced 3D backdrop (opt-in per campaign
  // via the enable_scene_diorama setting). Global kill-switch, dark-launched off —
  // the per-campaign setting only takes effect when this is on.
  ENABLE_SCENE_DIORAMA: import.meta.env.VITE_ENABLE_SCENE_DIORAMA === 'true',

  // BOU-3069 Layer 8: observe the backend's `input_ready` event (durable-answer
  // marker) without changing behaviour — telemetry/log only. Stage one.
  INPUT_READY_TELEMETRY: import.meta.env.VITE_INPUT_READY_TELEMETRY === 'true',

  // Stage two: let `input_ready` re-enable the composer, instead of waiting for
  // `turn_complete` (which lands only after the whole post-answer tail runs).
  // `turn_complete` still finalises streaming/card state either way.
  INPUT_READY_UNLOCKS_COMPOSER: import.meta.env.VITE_INPUT_READY_UNLOCKS_COMPOSER === 'true',

  // Debug mode
  DEBUG: import.meta.env.VITE_DEBUG === 'true' || import.meta.env.MODE === 'development',

};

/**
 * Prefix a relative API path with the configured backend URL.
 * Use instead of hardcoded fetch('/api/...') calls so requests
 * work when the frontend is served from a different origin (e.g. Firebase Hosting).
 *
 * Usage: fetch(apiUrl('/api/auth/registration-status'), { ... })
 */
export const apiUrl = (path) => `${API_CONFIG.BACKEND_URL}${path}`;
